import Vue from 'vue'
import Vuex from 'vuex'
import Headroom from 'headroom.js'
import LocomotiveScroll from 'locomotive-scroll'
import 'locomotive-scroll/dist/locomotive-scroll.css'
import { enableScrollLock, disableScrollLock } from 'muicss/lib/js/lib/util'
import gsap from 'gsap'

import * as components from './components/index'
import initSwiper from '../lib/init-swiper'
import initLazyImage from '../lib/lazy-image'
import simpleDropdown from '../lib/simple-dropdown'
import filtersMixin from './mixins/filters'
import windowSizeMixin from './mixins/window-size'
import preloaderLogoAnimation from '../gsap-animations/preloader-logo'
import { closeAnimation, openAnimation } from '../gsap-animations/menu'
import galleryMixin from './mixins/gallery-mixin'

Vue.use(Vuex)

const store = new Vuex.Store({
  state: {
    animate: false,
    smoothScroll: null,
  },
  mutations: {
    runAnimations(state) {
      state.animate = true
    },
    setSmoothScroll(state, smoothScroll) {
      state.smoothScroll = smoothScroll
    },
  },
})

const initVueApp = () => {
  new Vue({
    el: document.getElementById('vue-root'),
    store,
    components,
    mixins: [filtersMixin, windowSizeMixin, galleryMixin],
    data() {
      return {
        userWouldLikeMenuToBeOpened: false,
        menuIsOpened: false,
        loadMenuImage: false,
        activeAnimation: null,
        activePopup: '',
        header: {
          pinned: true,
          top: true,
        },
        scroll: null,
        heroSwiper: null,
        showPreloader: true,
      }
    },
    watch: {
      async userWouldLikeMenuToBeOpened() {
        if (!this.activeAnimation) {
          this.handleMenuToggle()
        }
      },
      activePopup() {
        if (!this.activePopup) {
          disableScrollLock()
        } else {
          enableScrollLock()
        }
      },
      menuIsOpened() {
        if (this.menuIsOpened && !this.loadMenuImage) {
          this.loadMenuImage = true
        }
      },
    },
    computed: {
      headerClasses() {
        if (!this.scroll || !this.scroll.smooth) {
          return null
        }

        return {
          'headroom--pinned': this.header.pinned,
          'headroom--unpinned': !this.header.pinned,
          'headroom--top': this.header.top,
          'headroom--not-top': !this.header.top,
        }
      },
    },
    async mounted() {
      document.addEventListener('keydown', this.handleKeyDown)

      window.addEventListener('resize', this.handleResize)
      initLazyImage(document.body)
      simpleDropdown(document.body, { openClass: 'accordion-item--active' })

      if (!('disableSmooth' in document.documentElement.dataset)) {
        setTimeout(() => {
          if (window.location.hash) {
            window.scrollTo(0, 0)
          }
          this.initSmoothScroll()
        }, 0)
      }

      // begin hero swiper
      const { heroSwiper } = initSwiper(document.body)

      this.heroSwiper = heroSwiper
      if (this.heroSwiper) {
        this.heroSwiper.autoplay.stop()
      }
      // end hero swiper

      // begin preloader
      window.addEventListener('beforeunload', this.handleBeforeUnload)
      await preloaderLogoAnimation()
      this.showPreloader = false
      this.$store.commit('runAnimations')
      if (this.heroSwiper) {
        this.heroSwiper.autoplay.start()
      }
      // end preloader
    },
    beforeDestroy() {
      document.removeEventListener('keydown', this.handleKeyDown)
      window.removeEventListener('resize', this.handleResize)
      window.removeEventListener('beforeunload', this.handleBeforeUnload)
    },
    methods: {
      handleMobileLangChange(e) {
        window.location = e.target.value
      },
      scrollTop() {
        if (this.scroll) {
          this.scroll.scrollTo('top')
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      },
      handleLocationOptionSelected(item) {
        if (item && item.code) {
          this.filters.code = item.code
        } else {
          this.filters.code = ''
        }
      },
      handleScroll(e) {
        this.header.pinned = e.direction !== 'down'
        this.header.top = e.scroll.y < 30
        if (this.$refs.heroScaleAnimation) {
          gsap.set(this.$refs.heroScaleAnimation, {
            scale: 1 + 0.5 * Math.min(1, e.scroll.y / this.windowHeight),
          })
        }
      },
      handleBeforeUnload() {
        this.showPreloader = true

        return null
      },
      async handleMenuToggle() {
        this.menuIsOpened = this.userWouldLikeMenuToBeOpened

        document.documentElement.classList.toggle(
          'menu-is-opened',
          this.menuIsOpened
        )

        if (!this.menuIsOpened) {
          this.$refs.popupMenu.scrollTo(0, 0)
          disableScrollLock()
          if (this.heroSwiper) {
            // trigger transition to next slide of hero slider, when menu closing animation is fired
            const { activeIndex, slides } = this.heroSwiper
            const index = (activeIndex + 1) % slides.length

            this.heroSwiper.slideTo(index)
            this.heroSwiper.autoplay.start()
          }
          this.activeAnimation = closeAnimation(this.$refs)
        } else {
          enableScrollLock()
          if (this.heroSwiper) {
            this.heroSwiper.autoplay.stop()
          }
          this.activeAnimation = openAnimation(this.$refs)
        }

        await this.activeAnimation
        this.activeAnimation = null

        // if user clicked on hamburger before animation finished
        if (this.userWouldLikeMenuToBeOpened !== this.menuIsOpened) {
          this.handleMenuToggle()
        }
      },
      handleKeyDown(e) {
        if (e.key === 'Escape') {
          this.activePopup = ''
          this.userWouldLikeMenuToBeOpened = false
        }
      },
      refreshScroll() {
        if (this.scroll) {
          setTimeout(() => this.scroll.update(), 0)
        }
      },
      headroomInstance() {
        const header = document.querySelector('[data-headroom]')
        const headroom = new Headroom(header, {
          offset: 100,
        })

        headroom.init()

        return headroom
      },
      initSmoothScroll() {
        this.scroll = new LocomotiveScroll({
          el: document.querySelector('[data-scroll-container]'),
          smooth: true,
          getDirection: true,
        })
        document.querySelectorAll('img[data-src]').forEach(img =>
          img.addEventListener('load', () => {
            this.refreshScroll()
          })
        )

        if (!this.scroll.smooth) {
          this.headroomInstance()
        }
        this.scroll.on('scroll', this.handleScroll)
        this.$store.commit('setSmoothScroll', this.scroll)

        const anchorLinks = document.querySelectorAll(
          'a[href^=\\#]:not([href$=\\#])'
        )

        anchorLinks.forEach(anchorLink => {
          const hashval = anchorLink.getAttribute('href')
          const target = document.querySelector(hashval)

          anchorLink.addEventListener('click', e => {
            e.preventDefault()
            e.stopPropagation()

            this.scroll.scrollTo(target)
          })
        })
      },
    },
  })
}

export default initVueApp
